import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useAceptarCotizacion } from "@/hooks/useInformes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";

/**
 * Tope del motivo. Es el MISMO número que `AceptarCotizacionRequest::MAXIMO_MOTIVO`
 * en el API: si se cambia uno hay que cambiar el otro, o la pantalla deja
 * escribir un texto que el servidor rechaza con un 422 recién al enviar.
 */
export const MAXIMO_MOTIVO = 500;

const aceptacionSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(1, "Indica cómo aceptó el cliente: es el respaldo de este registro.")
    .max(MAXIMO_MOTIVO, `El motivo no puede superar los ${MAXIMO_MOTIVO} caracteres.`),
});

type AceptacionValues = z.infer<typeof aceptacionSchema>;

interface ModalDeAceptacionManualProps {
  informeId: number;
  /** Para nombrar la propiedad en el encabezado: sin ella, "esta cotización". */
  propiedad?: string | null;
  abierto: boolean;
  onCerrar: () => void;
}

/**
 * Registrar a mano que el cliente aceptó la cotización (entrega C).
 *
 * Es el atajo para el cliente que acepta por teléfono, por correo o en persona
 * en vez de usar el enlace del correo de presupuesto.
 *
 * EL MOTIVO ES OBLIGATORIO, y la decisión merece explicarse porque contradice
 * la captura del prototipo, que muestra la modal sin campo de texto. Manda el
 * requisito escrito, y la razón es lo que se está guardando: no un dato sino el
 * TESTIMONIO de una persona sobre algo que ocurrió en otro canal. Ese
 * testimonio pone el informe en producción y le dispara un correo al cliente.
 * Sin el "cómo aceptó", la fila dice que alguien aceptó y no hay manera de
 * comprobarlo el día que el cliente lo discuta.
 *
 * La modal ADVIERTE las tres consecuencias antes de pedir el motivo. No es
 * cortesía: son irreversibles desde la pantalla —no hay "deshacer aceptación"—
 * y una de ellas sale del sistema hacia el cliente.
 *
 * La validación de acá es una comodidad, no la garantía: el servidor exige el
 * motivo igual, y también comprueba que a esta persona le toque registrar la
 * aceptación y que la cotización esté en un estado donde aceptar tenga sentido.
 */
export function ModalDeAceptacionManual({
  informeId,
  propiedad,
  abierto,
  onCerrar,
}: ModalDeAceptacionManualProps) {
  const aceptar = useAceptarCotizacion(informeId);

  const form = useForm<AceptacionValues>({
    resolver: zodResolver(aceptacionSchema),
    defaultValues: { motivo: "" },
  });

  // Pausada NO es en vuelo: sin red, TanStack deja la mutación `isPending` sin
  // haber enviado nada, y como el diálogo es modal y atrapa el foco, bloquear
  // el cierre solo por `isPending` encerraría al usuario hasta recuperar
  // conexión (mismo criterio que `EditarComentarioDialog`).
  const enVuelo = aceptar.isPending && !aceptar.isPaused;

  const cerrar = () => {
    // El formulario se limpia al cerrar y no al abrir: si quedara el texto de
    // un intento anterior, el siguiente informe arrancaría con el motivo de
    // otro cliente ya escrito y a un clic de guardarse.
    form.reset({ motivo: "" });
    onCerrar();
  };

  const enviar = (valores: AceptacionValues) => {
    aceptar.mutate(valores.motivo, {
      onSuccess: () => {
        toast.success("Aceptación registrada. El informe pasó a En proceso.");
        cerrar();
      },
      onError: (error) => {
        const errores = parseValidationErrors(error);

        // El 422 del motivo se pinta EN el campo. El de `estado` —"esta
        // cotización no se puede aceptar en su estado actual", o los feriados
        // del año que faltan— no tiene campo donde escribirse y va al toast,
        // igual que un 403 o un 500.
        if (errores?.motivo) {
          form.setError("motivo", { message: errores.motivo });
          return;
        }

        toast.error(
          getApiErrorMessage(error, "No pudimos registrar la aceptación. Inténtalo de nuevo."),
        );
      },
    });
  };

  return (
    <Dialog
      open={abierto}
      onOpenChange={(estaAbierto) => {
        if (!estaAbierto && !enVuelo) cerrar();
      }}
    >
      <DialogContent
        className="sm:max-w-[32.5rem]"
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Registrar aceptación manual</DialogTitle>
          <DialogDescription>
            Usa esta opción cuando el cliente acepta por teléfono, por correo o en persona,
            sin usar el enlace del correo de presupuesto.
          </DialogDescription>
        </DialogHeader>

        {/* Las consecuencias, antes de pedir nada. Dos de ellas no se pueden
            deshacer desde la pantalla y una sale del sistema hacia el cliente,
            así que se leen ANTES de escribir el motivo, no después. */}
        <div className="bg-muted/50 text-muted-foreground rounded-lg border p-4 text-sm">
          <p className="text-foreground mb-2 font-medium">Al registrarla:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              {propiedad ? <>El informe de {propiedad}</> : <>El informe</>} pasa a{" "}
              <span className="text-foreground font-medium">En proceso</span>.
            </li>
            {/* NO dice que el plazo empieza a correr: no empieza. Corre a
                contar de la visita de inspección, que es lo que promete el
                documento del cliente. Lo que aceptar sí hace es habilitar el
                portal por donde llegan los papeles con los que se sale a
                terreno. */}
            <li>
              Se habilita el portal donde el cliente carga sus antecedentes y datos de
              facturación.
            </li>
            <li>Se le envía al cliente el correo con los antecedentes que debe entregar.</li>
          </ul>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(enviar)} className="space-y-4">
            <FormField
              control={form.control}
              name="motivo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>¿Cómo aceptó el cliente?</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      maxLength={MAXIMO_MOTIVO}
                      placeholder="Ejemplo: llamó el 27-08 y confirmó con el gerente."
                      disabled={enVuelo}
                    />
                  </FormControl>
                  <FormDescription>
                    Queda registrado como respaldo de la aceptación.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={cerrar} disabled={enVuelo}>
                Cancelar
              </Button>
              <Button type="submit" disabled={enVuelo}>
                {enVuelo && <Spinner />}
                {enVuelo ? "Registrando…" : "Registrar aceptación"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
